import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { FloatingLabelPhoneInput } from 'components/floating-label-fields/floating-label-phone-input';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { FormValidationState, getPhoneNumberErrorMessage, isPhoneNumberValid } from 'utils/form';

const StyledFloatingLabelPhoneInput = styled(FloatingLabelPhoneInput)`
  min-width: 272px;
`;

interface IOrderPhoneInputProps {
  onValidStateChange?: (valid: boolean) => void;
}

const OrderPhoneInput: React.FC<IOrderPhoneInputProps> = ({ onValidStateChange = () => {} }) => {
  const { formatMessage } = useIntl();
  const { region } = useLocale();

  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [phoneInputTouched, setPhoneInputTouched] = useState<boolean>(false);
  const { orderPhoneNumber, setOrderPhoneNumber } = useOrderContext();

  const enableNumericPhoneValidation = useFlag(LaunchDarklyFlag.ENABLE_NUMERIC_PHONE_VALIDATION);

  useEffect(() => {
    let cancelled = false;

    const handleValidation = async () => {
      if (cancelled) {
        return;
      }

      const { valid, error } = await isPhoneNumberValid({
        phoneNumber: orderPhoneNumber,
        country: region,
        simpleValidation: enableNumericPhoneValidation,
      });

      if (valid) {
        setPhoneNumberError('');
      } else if (phoneInputTouched) {
        setPhoneNumberError(getPhoneNumberErrorMessage(error, formatMessage));
      }

      if (onValidStateChange) {
        onValidStateChange(valid);
      }
    };

    handleValidation();

    return () => {
      // cancel the async update to prevent overriding later updates
      cancelled = true;
    };
  }, [
    orderPhoneNumber,
    enableNumericPhoneValidation,
    region,
    formatMessage,
    phoneInputTouched,
    onValidStateChange,
  ]);

  return (
    <StyledFloatingLabelPhoneInput
      hideCountrySelector
      data-testid="order-phone-number"
      label={formatMessage({ id: 'phoneNumberRequired' })}
      onChange={setOrderPhoneNumber}
      onBlur={() => setPhoneInputTouched(true)}
      value={orderPhoneNumber}
      validation={
        phoneNumberError || !orderPhoneNumber
          ? FormValidationState.INVALID
          : FormValidationState.VALID
      }
      message={phoneNumberError}
    />
  );
};

export default OrderPhoneInput;
